import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import logoImage from '../assets/logo.png';

export function StyleGuide() {
  const brandColors = [
    { name: "Fun Orange", value: "#FF7F32", var: "--fun-orange", usage: "Primary actions, CTAs" },
    { name: "Fun Teal", value: "#1BA3A3", var: "--fun-teal", usage: "Secondary actions, accents" },
    { name: "Fun Navy", value: "#2E5984", var: "--fun-navy", usage: "Text, headings" },
    { name: "Fun Yellow", value: "#FFD54F", var: "--fun-yellow", usage: "Highlights, badges" },
    { name: "Fun Blue", value: "#4FC3F7", var: "--fun-blue", usage: "Info, links" },
    { name: "Fun Green", value: "#66BB6A", var: "--fun-green", usage: "Success states" }
  ];

  const spacingScale = [
    { name: "xs", value: "0.25rem", var: "--spacing-1" },
    { name: "sm", value: "0.5rem", var: "--spacing-2" },
    { name: "md", value: "0.75rem", var: "--spacing-3" },
    { name: "lg", value: "1rem", var: "--spacing-4" },
    { name: "xl", value: "1.5rem", var: "--spacing-6" },
    { name: "2xl", value: "2rem", var: "--spacing-8" },
    { name: "3xl", value: "3rem", var: "--spacing-12" },
    { name: "4xl", value: "4rem", var: "--spacing-16" },
    { name: "5xl", value: "5rem", var: "--spacing-20" }
  ];

  const typographyScale = [
    { element: "H1", class: "text-4xl font-semibold", usage: "Page titles" },
    { element: "H2", class: "text-3xl font-semibold", usage: "Section headers" },
    { element: "H3", class: "text-2xl font-medium", usage: "Subsection headers" },
    { element: "H4", class: "text-xl font-medium", usage: "Card titles" },
    { element: "Body Large", class: "text-lg font-normal", usage: "Large body text" },
    { element: "Body", class: "text-base font-normal", usage: "Regular body text" },
    { element: "Body Small", class: "text-sm font-normal", usage: "Captions, small text" },
    { element: "Button", class: "text-base font-medium", usage: "Button labels" }
  ];

  return (
    <div className="design-system-showcase bg-white">
      {/* Header */}
      <div className="bg-fun-navy text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <img 
            src={logoImage} 
            alt="Fun 'n Sun Logo" 
            className="h-20 w-auto mx-auto mb-6 brightness-0 invert"
          />
          <h1 className="text-4xl font-bold mb-4">Fun 'n Sun Design System</h1>
          <p className="text-xl opacity-90">Brand Guidelines & Style Guide</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16 space-y-16">
        
        {/* Brand Colors */}
        <section>
          <h2 className="text-3xl font-semibold mb-8 text-fun-navy">Brand Colors</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brandColors.map((color, index) => (
              <Card key={index} className="overflow-hidden">
                <div 
                  className="h-24 w-full"
                  style={{ backgroundColor: color.value }}
                />
                <CardContent className="p-4">
                  <h3 className="font-semibold text-fun-navy">{color.name}</h3>
                  <p className="text-sm text-muted-foreground mb-1">{color.value}</p>
                  <p className="text-sm text-muted-foreground mb-2">{color.var}</p>
                  <Badge variant="outline" className="text-xs">
                    {color.usage}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section>
          <h2 className="text-3xl font-semibold mb-8 text-fun-navy">Typography</h2>
          <Card>
            <CardHeader>
              <CardTitle>Font Family: Inter</CardTitle>
              <p className="text-muted-foreground">
                Primary typeface for all text elements. Clean, modern, and highly legible.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {typographyScale.map((type, index) => (
                <div key={index} className="border-b border-border pb-4 last:border-b-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">
                      {type.element}
                    </span>
                    <Badge variant="outline">{type.usage}</Badge>
                  </div>
                  <div className={type.class}>
                    The quick brown fox jumps over the lazy dog
                  </div>
                  <code className="text-xs text-muted-foreground mt-1 block">
                    {type.class}
                  </code>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        {/* Spacing Scale */}
        <section>
          <h2 className="text-3xl font-semibold mb-8 text-fun-navy">Spacing Scale</h2>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {spacingScale.map((space, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-16 text-sm font-medium text-fun-navy">
                      {space.name}
                    </div>
                    <div 
                      className="bg-fun-orange h-4"
                      style={{ width: space.value }}
                    />
                    <div className="text-sm text-muted-foreground">
                      {space.value} ({space.var})
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Components */}
        <section>
          <h2 className="text-3xl font-semibold mb-8 text-fun-navy">UI Components</h2>
          <div className="grid md:grid-cols-2 gap-8">
            
            {/* Buttons */}
            <Card>
              <CardHeader>
                <CardTitle>Buttons</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Primary</p>
                  <Button className="bg-fun-orange hover:bg-fun-orange/90">
                    Primary Button
                  </Button>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Secondary</p>
                  <Button variant="outline" className="border-fun-teal text-fun-teal hover:bg-fun-teal hover:text-white">
                    Secondary Button
                  </Button>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Sizes</p>
                  <div className="flex gap-2 items-center">
                    <Button size="sm">Small</Button>
                    <Button>Default</Button>
                    <Button size="lg">Large</Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Badges */}
            <Card>
              <CardHeader>
                <CardTitle>Badges</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-fun-orange text-white">Primary</Badge>
                  <Badge variant="secondary" className="bg-fun-teal text-white">Secondary</Badge>
                  <Badge variant="outline" className="border-fun-navy text-fun-navy">Outline</Badge>
                  <Badge className="bg-fun-yellow text-fun-navy">Highlight</Badge>
                  <Badge className="bg-fun-green text-white">Success</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Cards */}
            <Card>
              <CardHeader>
                <CardTitle>Cards</CardTitle>
              </CardHeader>
              <CardContent>
                <Card className="border-fun-blue/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-fun-navy">Sample Card</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      This is an example of a card component with the Fun 'n Sun styling.
                    </p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Brand Logo Usage */}
            <Card>
              <CardHeader>
                <CardTitle>Logo Usage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-white border rounded">
                  <img src={logoImage} alt="Logo on light background" className="h-12 w-auto" />
                  <p className="text-xs text-muted-foreground mt-2">On light backgrounds</p>
                </div>
                <div className="p-4 bg-fun-navy rounded">
                  <img src={logoImage} alt="Logo on dark background" className="h-12 w-auto brightness-0 invert" />
                  <p className="text-xs text-white/70 mt-2">On dark backgrounds</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Color Accessibility */}
        <section>
          <h2 className="text-3xl font-semibold mb-8 text-fun-navy">Color Combinations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-fun-orange text-white p-4 rounded-lg">
              <h3 className="font-semibold">Primary on White</h3>
              <p className="text-sm opacity-90">High contrast for CTAs</p>
            </div>
            <div className="bg-fun-teal text-white p-4 rounded-lg">
              <h3 className="font-semibold">Teal on White</h3>
              <p className="text-sm opacity-90">Secondary actions</p>
            </div>
            <div className="bg-fun-navy text-white p-4 rounded-lg">
              <h3 className="font-semibold">Navy Text</h3>
              <p className="text-sm opacity-90">Primary text color</p>
            </div>
            <div className="bg-fun-yellow text-fun-navy p-4 rounded-lg">
              <h3 className="font-semibold">Yellow Highlight</h3>
              <p className="text-sm opacity-80">Attention grabbing</p>
            </div>
            <div className="bg-fun-blue text-white p-4 rounded-lg">
              <h3 className="font-semibold">Info Blue</h3>
              <p className="text-sm opacity-90">Information states</p>
            </div>
            <div className="bg-fun-green text-white p-4 rounded-lg">
              <h3 className="font-semibold">Success Green</h3>
              <p className="text-sm opacity-90">Success states</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}